import type { TextSectionProps } from "@/scott/ui/blocks/text-section/text-section";
import type { VideoBlockProps } from "@/scott/ui/blocks/video/video";
import {
  adaptMedia,
  CLOUDINARY_TRANSFORMATION_D2C_ULTRAWIDE,
  CLOUDINARY_TRANSFORMATION_D2C_WIDESCREEN,
  getBlockId,
  getCloudinarySrcSet,
  resolveTransform,
} from "@/services/contentful/assemblies/page/adapter/utils";
import {
  UiConfigurationAdapter,
  type UiConfigurationInput,
} from "@/services/contentful/topics/UIConfiguration/ui-configuration.adapter";
import type { SectionMediaPageBlock } from "@/types/page-block";

const columnWidthMap = {
  narrow: 8,
  medium: 10,
  wide: 12,
} as const satisfies Record<
  SectionMediaInput["containerWidth"],
  NonNullable<TextSectionProps["columnWidth"]>
>;

// Use the configured aspect ratio and crop consistently across viewports.
const MEDIA_TRANSFORMS = {
  "16:9": CLOUDINARY_TRANSFORMATION_D2C_WIDESCREEN,
  "21:9": CLOUDINARY_TRANSFORMATION_D2C_ULTRAWIDE,
} as const;

// Approximate rendered width per layout, for the <img>/<source> sizes attribute.
function getMediaSizes(
  fullWidth: boolean | undefined,
  columnWidth: 8 | 10 | 12
): string {
  // biome-ignore lint/style/noMagicNumbers: "number used only for media query breakpoints"
  if (fullWidth || columnWidth === 12) {
    return "100vw";
  }

  // biome-ignore lint/style/noMagicNumbers: "number used only for media query breakpoints"
  if (columnWidth === 8) {
    return "(min-width: 768px) 67vw, 83vw";
  }
  return "83vw";
}

export interface SectionMediaInput {
  sys?: { id: string };
  __typename: "SectionMedia";
  internalName: string;
  fullWidth?: boolean;
  containerWidth: "narrow" | "medium" | "wide";
  aspectRatio?: "21:9" | "16:9";
  progressbarColor?: "darkteal" | "blazeorange" | "neonchartreuse" | null;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
  showControls?: boolean;
  backgroundVideo?: boolean;
  uiConfiguration?: UiConfigurationInput;
  anchorId?: string;
  headline: VideoBlockProps["headline"];
  headlineColor?: string | null;
  media?: {
    id?: string;
    __typename?: "MediaImage" | "MediaVideo" | null;
    alternativeText?: string | null;
    caption?: string | null;
    hasFocalPoint?: boolean;
    image?: unknown;
    video?: unknown;
    externalVideoUrl?: string | null;
    internalName?: string | null;
  } | null;
}

export function SectionMediaAdapter(
  item: SectionMediaInput,
  index: number
): SectionMediaPageBlock {
  const media = adaptMedia(item.media ?? undefined);

  if (media?.type === "Image") {
    const aspectRatio = item.aspectRatio ?? "16:9";
    const focal = item.media?.hasFocalPoint;
    const transform =
      MEDIA_TRANSFORMS[aspectRatio] ?? CLOUDINARY_TRANSFORMATION_D2C_WIDESCREEN;

    const desktop = getCloudinarySrcSet(media.src, {
      transform: resolveTransform(transform, focal),
    });

    const mobile = getCloudinarySrcSet(media.src, {
      transform: resolveTransform(transform, focal),
    });

    if (desktop) {
      media.src = desktop.src;
      media.srcSet = desktop.srcSet;
    }
    if (mobile) {
      media.mobileSrc = mobile.src;
      media.mobileSrcSet = mobile.srcSet;
    }
    media.sizes = getMediaSizes(
      item.fullWidth,
      columnWidthMap[item.containerWidth]
    );
  }

  return {
    __typename: item.__typename,
    sys: item.sys,
    id: getBlockId(item, index),
    internalName: item.internalName,
    headline: item.headline,
    headlineColor: item.headlineColor ?? undefined,
    media,
    fullWidth: item.fullWidth,
    columnWidth: columnWidthMap[item.containerWidth],
    aspectRatio: item.aspectRatio,
    progressbarColor: item.progressbarColor ?? undefined,
    autoplay: item.autoplay,
    muted: item.muted,
    loop: item.loop,
    showControls: item.showControls,
    backgroundVideo: item.backgroundVideo,
    anchorId: item.anchorId,
    uiConfiguration: item.uiConfiguration
      ? UiConfigurationAdapter(item.uiConfiguration)
      : undefined,
  };
}
